//////////////////
// IMPORTS
//////////////////

// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";

// one-on-one ap imports
import { IWebAppProps } from "./OneOnOneAutopilot.d";
import { OneOnOneAutopilotView } from "./OneOnOneAutopilotView";
import { orgWithOneOnOnesFragment, oneOnOneAutopilotBaseQuery } from "./OneOnOneAutopilot.gql";

// utils & constants
import { getCurrentOrg } from "@util/org.util";
import { page, PageEvents } from "@util/analytics.util";

// routing
import { withRouteQuery } from "@routing/routes.util";

// styles
import { styles } from "./OneOnOnAutopilot.styles";
import withStyles from "@material-ui/core/styles/withStyles";

// components
import { LoaderClockWithMessage } from "@clockwise/web-commons/src/components/loader-clock-with-message";
import { OneOnOneAutopilotCanAutopilotQueryApolloDocument } from "./graphql/__generated__/CanAutopilot.generated";
import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";

const OneOnOneLoader = () => {
  return (
    <div style={{ height: "100vh", display: "flex" }}>
      <LoaderClockWithMessage key="loader" message="Loading your One-On-Ones" />
    </div>
  );
};
//////////////////
// COMPONENT
//////////////////
const OneOnOneAutopilotComponent = (props: IWebAppProps) => {
  const { viewer } = props;
  const org = getCurrentOrg(viewer);

  const oneOnOneEventsResponse =
    (org?.oneOnOnesForCalendar?.__typename === "EventList" && org.oneOnOnesForCalendar.list) || [];

  const { data, loading } = useQuery(OneOnOneAutopilotCanAutopilotQueryApolloDocument, {
    variables: {
      orgRelayId: org?.id!,
      userCalendarIds: [org?.primaryOrgCalendar!],
      externalEventIds: oneOnOneEventsResponse.map((event) => event.eventKey.externalEventId),
    },
    skip: !org || !org?.primaryOrgCalendar,
  });

  // componentDidMount
  React.useEffect(() => {
    page(PageEvents.ONE_ON_ONES_AUTOPILOT);
  }, []);

  if (loading) {
    return OneOnOneLoader();
  }

  if (!org) {
    return OneOnOneLoader();
  }

  const rendererOrg = getValue(data?.org, "Org");

  if (!rendererOrg) {
    return OneOnOneLoader();
  }

  const canAutopilotList =
    (rendererOrg &&
      rendererOrg.canScheduleResponse &&
      rendererOrg.canScheduleResponse.__typename === "CanScheduleResponseList" &&
      rendererOrg.canScheduleResponse.list) ||
    [];
  const canAutopilotMap = canAutopilotList.reduce(
    (mapper: { [key: string]: boolean }, curr: ISchema.ICanScheduleResponse) => {
      mapper[curr.externalEventId] = curr.canAutopilot;
      return mapper;
    },
    {},
  );

  return (
    <div className="cw-max-w-screen-lg cw-min-w-[320px] cw-h-screen">
      <React.Fragment>
        <OneOnOneAutopilotView
          org={org}
          relay={props.relay}
          oneOnOnesForCalendar={oneOnOneEventsResponse}
          canAutopilotMap={canAutopilotMap}
        />
      </React.Fragment>
    </div>
  );
};

//////////////////
// STYLES
//////////////////

export const OneOnOneAutopilotComponentStyled = withStyles(styles)(OneOnOneAutopilotComponent);

///////////
// Relay //
///////////

export const OneOnOneAutopilot = createFragmentContainer(
  OneOnOneAutopilotComponentStyled,
  orgWithOneOnOnesFragment,
);

export const OneOnOneAutopilotPage = withRouteQuery(
  OneOnOneAutopilot,
  oneOnOneAutopilotBaseQuery,
  undefined,
  OneOnOneLoader,
);
